import React, { useState } from "react";
import { Select, MenuItem, Slider, TextField } from "@mui/material";

const VideoSettings = () => {
    const [framework, setFramework] = useState("");
    const [videoLength, setVideoLength] = useState(150);
    const [orientation, setOrientation] = useState("landscape");
    const [clipPace, setClipPace] = useState("medium");

    const formatDuration = (value) => {
        if (value < 60) return `${value} sec`;
        const minutes = Math.floor(value / 60);
        const seconds = value % 60;
        return `${minutes} min ${seconds > 0 ? `${seconds} sec` : ""}`.trim();
    };

    const paceOptions = [
        {
            value: "fast",
            label: "Fast",
            description: "Changes every 5 - 15 words",
        },
        {
            value: "medium",
            label: "Medium",
            description: "Changes every 10 - 20 words",
        },
        {
            value: "slow",
            label: "Slow",
            description: "Changes every 15 - 30 words",
        },
        {
            value: "very-slow",
            label: "Very Slow",
            description: "Changes every 20 - 40 words",
        },
    ];

    return (
        <div className="space-y-8 mt-[40px] font-[Outfit] text-gray-800">
            {/* Story Framework */}
            <div className="w-full">
                <div className="mb-4">
                    <div className="text-xl font-medium">Story Framework</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Define the narrative structure for your video content.
                    </div>
                </div>
                <div className="max-w-4xl w-full">
                    <TextField
                        multiline
                        rows={4}
                        value={framework}
                        onChange={(e) => setFramework(e.target.value)}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter your story structure..."
                        className="font-[Outfit] w-full"
                    />
                </div>
            </div>

            <div className="border-b border-gray-300 my-4" />

            {/* Video Length with visible duration */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Video Length</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Set maximum duration (15-300 seconds)
                    </div>
                </div>
                <div className="w-64">
                    <div className="text-right text-lg font-medium mb-2 text-gray-700">
                        {formatDuration(videoLength)}
                    </div>
                    <Slider
                        value={videoLength}
                        onChange={(e, newValue) => setVideoLength(newValue)}
                        valueLabelFormat={formatDuration}
                        valueLabelDisplay="auto"
                        min={15}
                        max={300}
                        step={15}
                        className="mt-2"
                    />
                </div>
            </div>

            <div className="border-b border-gray-300 my-4" />

            {/* Orientation */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Orientation</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Choose between portrait or landscape format
                    </div>
                </div>
                <Select
                    value={orientation}
                    onChange={(e) => setOrientation(e.target.value)}
                    className="w-40 font-[Outfit] text-xl"
                    size="small"
                >
                    <MenuItem value="landscape">Landscape</MenuItem>
                    <MenuItem value="portrait">Portrait</MenuItem>
                </Select>
            </div>

            <div className="border-b border-gray-300 my-4" />

            {/* Clip Change Pace */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Clip Change Pace</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Control how frequently video clips change based on
                        script words
                    </div>
                </div>
                <Select
                    value={clipPace}
                    onChange={(e) => setClipPace(e.target.value)}
                    className="w-64 font-[Outfit] text-xl"
                    size="small"
                    renderValue={(selected) => {
                        const option = paceOptions.find(
                            (o) => o.value === selected
                        );
                        return (
                            <div className="flex flex-col">
                                <span className="font-medium">
                                    {option?.label}
                                </span>
                                <span className="text-gray-500 text-sm">
                                    {option?.description}
                                </span>
                            </div>
                        );
                    }}
                >
                    {paceOptions.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            className="py-3"
                        >
                            <div className="flex flex-col">
                                <span className="font-medium text-base">
                                    {option.label}
                                </span>
                                <span className="text-gray-500 text-sm mt-1">
                                    {option.description}
                                </span>
                            </div>
                        </MenuItem>
                    ))}
                </Select>
            </div>
        </div>
    );
};

export default VideoSettings;
